import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "nursery-propagation-systems",
  kind: "domain-vertical",
  title: "Nursery & Propagation Management Systems",
  shortTitle: "Nursery Management",
  status: "draft",
  tier: "we-do",
  seoTitle: "Nursery & Propagation Management Systems | GA",
  seoDescription:
    "Greenhouse bench planning, seed flat tracking, germination schedules, and nursery-to-field transplant management for commercial growers.",
  summary:
    "Greenhouse bench space allocation, seed flat tracking, germination monitoring, and transplant timelines that coordinate nursery production with field readiness.",
  image: "/images/capabilities/software-architecture-engineering-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "operational-task-management",
    "dashboard-design",
    "succession-calendars-living-inventories",
  ],
  engineering: [
    "Greenhouse bench space allocation and square-footage utilization modeling across production cycles",
    "Seed flat, plug tray, and soil block tracking with cell density, sow date, and batch identification",
    "Germination chamber scheduling, heat mat temperature management, and emergence percentage tracking",
    "Staging, potting-up, and hardening-off workflow state machines linking nursery steps to weather forecasts",
    "Nursery-to-field transplant dispatching coordinating greenhouse pull dates with field bed readiness",
    "Seed lot consumption tracking recording germination rates and seed inventory depletion per sowing",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Greenhouse Space Is Premium Production Real Estate",
    },
    {
      type: "paragraph",
      text: "In vegetable and specialty crop operations, the propagation greenhouse represents the highest-density, most capital-intensive production area on the farm. Sowing thousands of seedling plugs requires precise timing: starting seeds too early leads to root-bound, stressed plants waiting for cold field soils to warm, while sowing too late misses prime market windows. Managing bench capacity across dozens of cultivars requires explicit spatial and temporal tracking.",
    },
    {
      type: "heading",
      level: 2,
      text: "Synchronizing Nursery Production with Field Readiness",
    },
    {
      type: "paragraph",
      text: "A disciplined propagation system tracks seedling batches from seed packet to germination chamber, bench staging, potting up, hardening off, and final field transplanting. By tying seedling growth curves directly to field bed availability, growers can identify upcoming bench shortages weeks in advance and adjust sowing cadences to match operational reality.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit nursery facilities, greenhouse bench square footage, flat types, propagation methods, and transplant schedules.",
      produces: [
        "Nursery bench geometry and greenhouse facility capacity audit",
        "Tray type, cell density, and soil-mix specification inventory",
        "Cultivar propagation timeline and germination temperature requirement catalog",
        "Nursery workflow bottleneck and seasonal space-constraint analysis",
      ],
      fit: {
        good: [
          "Commercial vegetable growers, greenhouse operations, and plant nurseries managing intensive plug and transplant production",
          "Farms seeking to eliminate nursery bench bottlenecks and align seedling production with field bed availability",
        ],
        poor: [
          "Direct-seeded crop operations that do not utilize transplanting or greenhouse propagation",
          "Casual hobbyists requiring only simple reminder notes for a few backyard seed trays",
        ],
      },
    },
    "initial-design": {
      focus:
        "Model nursery bench spatial allocations, batch lifecycle state machines, flat inventory, and transplant schedules.",
      produces: [
        "Nursery spatial data model connecting greenhouses, benches, heat mats, and hardening zones",
        "Seedling batch state machine schema (sown, germinated, potted-up, hardened, ready, transplanted)",
        "Tray-density and cell-count mathematical models",
        "Bench utilization visualizer and sowing schedule interface designs",
      ],
    },
    "detailed-design": {
      focus:
        "Specify batch tracking identifiers, greenhouse-to-field handoff APIs, germination logging, and inventory rules.",
      produces: [
        "Unique batch identification and barcode/QR label specifications for seed flats",
        "Automated transplant date calculation algorithms based on sow date and cultivar vigor",
        "Greenhouse bench capacity reservation and collision-detection algorithms",
        "Seed packet inventory deduction and germination rate tracking contracts",
      ],
    },
    implementation: {
      focus:
        "Deploy the nursery management system, digitize bench layouts, configure tray types, and train greenhouse staff.",
      produces: [
        "Production nursery and propagation management application",
        "Configured greenhouse bench layouts and capacity monitoring tools",
        "Active seedling batch registers and nursery task queues",
        "Standard operating procedures for greenhouse sowing, potting-up, and transplant dispatch",
      ],
    },
  },
};
